import {Action} from '@reduxjs/toolkit'
import {put, takeLatest, select, call} from 'redux-saga/effects'
import {
  getLeadStatuses,
  createLeadStatus,
  updateLeadStatus,
  deleteLeadStatus,
} from './LeadStatusCRUD'
import {LeadStatusModel} from '../models/LeadStatusModel'

export interface ActionWithPayload<T> extends Action {
  payload?: T
}

export const actionTypes = {
  // Fetch via API
  FetchPage: '[LeadStatus] FetchPage Action',
  // Set the page fetched via API
  SetPage: '[LeadStatus] SetPage Action',
  // Create new lead status
  CreateLeadStatus: '[LeadStatus] CreateLeadStatus Action',
  // Update lead status
  UpdateLeadStatus: '[LeadStatus] UpdateLeadStatus Action',
  // Delete lead status
  DeleteLeadStatus: '[LeadStatus] DeleteLeadStatus Action',
}

const initialLeadStatusState: ILeadStatusState = {
  currentPage: 1,
  leadStatuses: [],
  totalLeadStatuses: 0,
  leadStatusesPerPage: 10,
  visiblePageList: [],
  totalPages: 0,
  isLoading: false,
  error: null,
}

export interface ILeadStatusState {
  currentPage: number
  totalLeadStatuses: number
  leadStatuses: LeadStatusModel[]
  leadStatusesPerPage: number
  visiblePageList: number[]
  totalPages: number
  isLoading: boolean
  error: string | null
}

export const reducer = (
  state: ILeadStatusState = initialLeadStatusState,
  action: ActionWithPayload<any>
) => {
  switch (action.type) {
    case actionTypes.SetPage: {
      return {...state, ...action.payload, isLoading: false, error: null}
    }
    default:
      return state
  }
}

export const actions = {
  fetchPage: (currentPage: number) => ({type: actionTypes.FetchPage, payload: {currentPage}}),
  setPage: (
    leadStatuses: LeadStatusModel[],
    currentPage: number,
    totalLeadStatuses: number,
    visiblePageList: number[],
    totalPages: number
  ) => ({
    type: actionTypes.SetPage,
    payload: {
      leadStatuses,
      currentPage,
      totalLeadStatuses,
      visiblePageList,
      totalPages,
      isLoading: false,
    },
  }),
  createLeadStatus: (status: string) => ({
    type: actionTypes.CreateLeadStatus,
    payload: {status},
  }),
  updateLeadStatus: (statusId: string, data: any) => ({
    type: actionTypes.UpdateLeadStatus,
    payload: {statusId, data},
  }),
  deleteLeadStatus: (statusId: string) => ({
    type: actionTypes.DeleteLeadStatus,
    payload: {statusId},
  }),
}

export function* saga() {
  yield takeLatest(
    actionTypes.CreateLeadStatus,
    function* createLeadStatusHandler(action: ActionWithPayload<any>) {
      try {
        // @ts-ignore
        const stateSelectFn = (state) => {
          return {
            subscriberId: state.auth.user.subscriberId,
          }
        }

        // @ts-ignore
        let selectedState: any = yield select(stateSelectFn)

        yield createLeadStatus(selectedState.subscriberId, {
          status: action.payload.status,
        })

        // Refresh the lead statuses list after creating a new status
        yield put(actions.fetchPage(1))
      } catch (error) {
        console.error('Error creating lead status:', error)
      }
    }
  )

  yield takeLatest(
    actionTypes.UpdateLeadStatus,
    function* updateLeadStatusHandler(action: ActionWithPayload<any>) {
      try {
        // @ts-ignore
        const stateSelectFn = (state) => {
          return {
            subscriberId: state.auth.user.subscriberId,
          }
        }

        // @ts-ignore
        let selectedState: any = yield select(stateSelectFn)

        yield updateLeadStatus(
          selectedState.subscriberId,
          action.payload.statusId,
          action.payload.data
        )

        // Refresh the lead statuses list after updating a status
        yield put(actions.fetchPage(1))
      } catch (error) {
        console.error('Error updating lead status:', error)
      }
    }
  )

  yield takeLatest(
    actionTypes.DeleteLeadStatus,
    function* deleteLeadStatusHandler(action: ActionWithPayload<any>) {
      try {
        // @ts-ignore
        const stateSelectFn = (state) => {
          return {
            subscriberId: state.auth.user.subscriberId,
          }
        }

        // @ts-ignore
        let selectedState: any = yield select(stateSelectFn)

        yield deleteLeadStatus(selectedState.subscriberId, action.payload.statusId)

        // Refresh the lead statuses list after deleting a status
        yield put(actions.fetchPage(1))
      } catch (error) {
        console.error('Error deleting lead status:', error)
      }
    }
  )

  yield takeLatest(actionTypes.FetchPage, function* pageRequested(action: ActionWithPayload<any>) {
    try {
      // @ts-ignore
      const stateSelectFn = (state) => {
        return {
          subscriberId: state.auth.user.subscriberId,
          leadStatusesPerPage: state.leadStatus?.leadStatusesPerPage || 10,
          visiblePageList: state.leadStatus?.visiblePageList || [],
        }
      }

      // @ts-ignore
      let requestedPage: number = action.payload.currentPage
      let selectedState: any = yield select(stateSelectFn)
      const {data: leadStatuses, headers} = yield getLeadStatuses(
        selectedState.subscriberId,
        requestedPage
      )

      let totalLeadStatuses: number = parseInt(headers['x-total-count'] || '0')
      let leadStatusesPerPage: number = selectedState.leadStatusesPerPage
      let totalPages: number =
        Math.floor(totalLeadStatuses / leadStatusesPerPage) +
        Math.ceil((totalLeadStatuses % leadStatusesPerPage) / leadStatusesPerPage)

      let visiblePageList: number[] = []

      let minVisible: number = 1
      let maxVisible: number = Math.min(5, totalPages)

      // Update visible page list
      if (
        requestedPage < selectedState.visiblePageList[0] ||
        selectedState.visiblePageList.length === 0
      ) {
        minVisible = requestedPage
        maxVisible = Math.min(minVisible + 4, totalPages)
      } else if (
        requestedPage > selectedState.visiblePageList[selectedState.visiblePageList.length - 1]
      ) {
        maxVisible = requestedPage
        minVisible = Math.max(maxVisible - 4, 1)
      } else {
        minVisible = selectedState.visiblePageList[0]
        maxVisible = selectedState.visiblePageList[selectedState.visiblePageList.length - 1]
      }

      for (let i = minVisible; i <= maxVisible; i++) {
        visiblePageList.push(i)
      }

      yield put(
        actions.setPage(leadStatuses, requestedPage, totalLeadStatuses, visiblePageList, totalPages)
      )
    } catch (error) {
      console.error('Error fetching lead statuses:', error)
    }
  })
}
